import type { User } from "@repo/fika-shared/src/loader/user-schema.js"
import type { Replicache } from "@rocicorp/replicache"
import type { SessionDetail } from "@teamhanko/hanko-elements"
import {
  createContext,
  createMemo,
  onMount,
  type ParentProps,
  Show,
  useContext,
} from "solid-js"
import type { DB } from "@/data/database"
import type { M } from "@/data/mutators"
import { initPoke } from "@/data/poke"
import { initReplicache } from "@/data/replicache"
import { Icon } from "@/designSystem/components/icon"
import { syncClass } from "./app.css"

type App = {
  db: DB
  replicache: Replicache<M>
  session: SessionDetail
  user: User
}
const AppContext = createContext<App>()

export function useAppContext() {
  const context = useContext(AppContext)

  if (!context) {
    throw new Error("useAppContext used without a Provider!")
  }

  return context
}

type Props = ParentProps & {
  session: SessionDetail
}
export function AppProvider(props: Props) {
  const { replicache, db, loaded } = initReplicache(props.session)
  const getUser = createMemo(() => db.user.get(props.session.userID))

  onMount(() => {
    initPoke(getUser, replicache)
  })

  return (
    <Show
      when={getUser()}
      fallback={
        <div class={syncClass}>
          <Icon name="download-cloud" color="emerald800" />
          Syncing to this device...
        </div>
      }
    >
      {user => (
        <AppContext.Provider
          value={{ db, replicache, session: props.session, user: user() }}
        >
          <Show when={loaded()}>{props.children}</Show>
        </AppContext.Provider>
      )}
    </Show>
  )
}
